import { AlertTriangle, CheckCircle, Info, X, XCircle } from "lucide-react";
import type { ReactNode } from "react";

type AlertBannerVariant = "warning" | "error" | "info" | "success";

interface AlertBannerViewProps {
  variant: AlertBannerVariant;
  dismissible?: boolean;
  onDismiss?: () => void;
  children: ReactNode;
}

const variantStyles: Record<AlertBannerVariant, string> = {
  warning:
    "border-warning-border/60 bg-gradient-to-r from-warning-subtle/95 via-white/80 to-warning-subtle/95 text-warning-text",
  error:
    "border-destructive-border/60 bg-gradient-to-r from-destructive-subtle/95 via-white/80 to-destructive-subtle/95 text-destructive-text",
  info: "border-info-border/60 bg-gradient-to-r from-info-subtle/95 via-white/80 to-info-subtle/95 text-info-text",
  success: "border-success-border/60 bg-success-subtle text-success-text",
};

const iconStyles: Record<AlertBannerVariant, string> = {
  warning: "text-warning",
  error: "text-destructive",
  info: "text-info",
  success: "text-success",
};

const textStyles: Record<AlertBannerVariant, string> = {
  warning: "text-warning-text",
  error: "text-destructive-text",
  info: "text-info-text",
  success: "text-success-text",
};

const dismissStyles: Record<AlertBannerVariant, string> = {
  warning: "text-warning/70 hover:text-warning-text",
  error: "text-destructive/70 hover:text-destructive-text",
  info: "text-info hover:text-info-text",
  success: "text-success/70 hover:text-success-text",
};

const Icon = ({ variant }: { variant: AlertBannerVariant }) => {
  const className = `h-8 w-8 shrink-0 ${iconStyles[variant]}`;
  switch (variant) {
    case "error":
      return <XCircle className={className} />;
    case "info":
      return <Info className={className} />;
    case "success":
      return <CheckCircle className={className} />;
    default:
      return <AlertTriangle className={className} />;
  }
};

export const BannerNotificationView = ({
  variant,
  dismissible = false,
  onDismiss,
  children,
}: AlertBannerViewProps) => {
  return (
    <div
      className={`w-full border-b backdrop-blur-md transition-all duration-500 animate-in fade-in slide-in-from-top-full ${variantStyles[variant]}`}
    >
      <div className="mx-auto flex min-h-11 items-center justify-between px-4 py-1.5 sm:px-6 lg:px-6">
        <div className="flex flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
          <div
            className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm font-medium ${textStyles[variant]}`}
          >
            <Icon variant={variant} />
            {children}
          </div>
        </div>
        {dismissible && onDismiss && (
          <button
            onClick={onDismiss}
            className={`ml-4 shrink-0 transition-colors duration-200 ${dismissStyles[variant]}`}
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
